import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertTitle, Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Archive, ArchiveRestore, ArrowRight, ChevronLeft, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { useFeatureFlag } from '../../contexts/featureFlagContext';
import { useArchiveProject, useDeleteProject, useUnarchiveProject } from '../../hooks/useProjects';
import { projectApiConnect } from '../../lib/api/projectApi.connect';
import { proposalProjectApiConnect } from '../../lib/api/proposalProjectApi.connect';
import { useFlagsReady } from '../../lib/connect';
import { ProjectDataError } from '../../lib/errors/ProjectDataError';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { PageHeader } from '../PageHeader';
import { ChatTab } from './ChatTab';
import { CLINsTab } from './CLINsLCATs/CLINsTab';
import { LCATsTab } from './CLINsLCATs/LCATsTab';
import { ComplianceMatrixTab } from './ComplianceMatrixTab';
import { ConvertToContractDialog } from './ConvertToContractDialog';
import { ConvertToPreAwardDialog } from './ConvertToPreAwardDialog';
import { FilesTab } from './FilesTab';
import { MasterMatrixTab } from './MasterMatrixTab';
import { PricingTab } from './PricingTab';
import { QATab } from './QATab';
import { ShredViewTab } from './ShredViewTab';
import { TeamTab } from './TeamTab';
const SET_ASIDE_LABELS = {
    small_business: 'Small Business',
    '8a': '8(a)',
    hubzone: 'HUBZone',
    sdvosb: 'SDVOSB',
    wosb: 'WOSB',
    unrestricted: 'Unrestricted',
    other: 'Other'
};
function formatDeadline(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}
/**
 * Transform API response to ProjectDetail format with validation
 */ function transformToProjectDetail(response) {
    // Validate response structure
    if (!response.project?.projectId) {
        throw new ProjectDataError('Invalid project response: missing projectId', 'MISSING_FIELD', 'projectId', response);
    }
    if (!response.project.name) {
        throw new ProjectDataError('Invalid project response: missing name', 'MISSING_FIELD', 'name', response);
    }
    // Transform with all required fields
    const projectDetail = {
        projectId: response.project.projectId,
        name: response.project.name,
        description: response.project.description,
        type: response.project.type,
        status: response.project.status,
        createdAt: response.project.createdAt,
        lastActivityAt: response.project.lastActivityAt,
        members: response.members || [],
        role: response.role,
        memberCount: response.memberCount
    };
    // Validate transformed data
    if (!projectDetail.projectId) {
        throw new ProjectDataError('Transformation failed: projectId is empty after assignment', 'TRANSFORMATION_FAILED', 'projectId');
    }
    return projectDetail;
}
export function ProposalDashboard() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Escape hatch: ?legacy=1 forces the old 10-tab dashboard even when the
    // V2 flag is enabled. Useful as an emergency rollback during the prod
    // rollout window — removed in PR14 once V2 is the only path.
    const legacyOverride = searchParams.get('legacy') === '1';
    const { user } = useAuth();
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [preAwardDialogOpen, setPreAwardDialogOpen] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const archiveProject = useArchiveProject();
    const unarchiveProject = useUnarchiveProject();
    const deleteProject = useDeleteProject();
    useEffect(()=>{
        setShowBreadcrumbs(false);
        return ()=>setShowBreadcrumbs(true);
    }, [
        setShowBreadcrumbs
    ]);
    const handleArchive = async ()=>{
        if (!id) return;
        try {
            await archiveProject.mutateAsync(id);
            toast.success('Proposal archived successfully');
            navigate('/projects');
        } catch (error) {
            // Use error.message verbatim — Connect throws ConnectError and REST
            // The fallback only kicks in if a non-Error value was thrown.
            toast.error(error instanceof Error ? error.message : 'Failed to archive proposal');
        }
    };
    const handleUnarchive = async ()=>{
        if (!id) return;
        try {
            await unarchiveProject.mutateAsync(id);
            toast.success('Proposal unarchived successfully');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to unarchive proposal');
        }
    };
    const handleDelete = async ()=>{
        if (!id) return;
        try {
            await deleteProject.mutateAsync(id);
            toast.success('Proposal deleted permanently');
            setShowDeleteDialog(false);
            navigate('/projects');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to delete proposal');
        }
    };
    const flagsReady = useFlagsReady();
    const dashboardV2Flag = useFeatureFlag('proposal_dashboard_v2');
    const dashboardV2Enabled = dashboardV2Flag ?? true; // default ON
    const { data: projectData, isLoading, error } = useQuery({
        queryKey: [
            'project',
            id
        ],
        queryFn: async ()=>{
            if (!id) throw new Error('Project ID is required');
            const response = await projectApiConnect.getProject(id);
            return {
                project: transformToProjectDetail(response),
                solicitation: response.solicitation
            };
        },
        enabled: !!id,
        staleTime: 30000
    });
    const project = projectData?.project;
    const solicitation = projectData?.solicitation;
    const queryClient = useQueryClient();
    const [resyncing, setResyncing] = useState(false);
    const handleResync = useCallback(async ()=>{
        if (!project || resyncing) return;
        setResyncing(true);
        try {
            const result = await proposalProjectApiConnect.resyncExtractedData(project.projectId, user?.tenant_id || '');
            toast.success(`Resynced: ${result.clinsSynced} CLINs, ${result.lcatsSynced} LCATs, ${result.requirementsSynced} requirements, ${result.classificationsSynced} classifications`);
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    id
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projectFiles',
                    project.projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'clins'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'lcats'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'complianceMatrix'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'shredView'
                ]
            });
        } catch (err) {
            toast.error(`Resync failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally{
            setResyncing(false);
        }
    }, [
        project,
        resyncing,
        queryClient,
        id,
        user?.tenant_id
    ]);
    // V2 dashboard flag: once Flipt has loaded, redirect to the V2 shell so the
    // user lands on /proposal/:id/v2/home instead of the legacy 10-tab UI.
    // The `?legacy=1` escape hatch bypasses the redirect — used during the
    // prod rollout window for emergency fallback to the old shell.
    if (flagsReady && dashboardV2Enabled && id && !legacyOverride) {
        return /*#__PURE__*/ _jsx(Navigate, {
            to: `/proposal/${id}/v2/home`,
            replace: true
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-muted-foreground mb-1",
                                children: "Loading..."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-48 bg-gray-200 rounded animate-pulse"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center h-64",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "text-gray-500",
                        children: "Loading proposal..."
                    })
                })
            ]
        });
    }
    if (error || !project) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Proposal not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center h-64",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-2",
                                children: "This proposal doesn't exist or you don't have access."
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                to: "/projects",
                                className: "text-blue-600 hover:underline",
                                children: "Back to Projects"
                            })
                        ]
                    })
                })
            ]
        });
    }
    if (project.type !== 'proposal') {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Invalid Proposal"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center h-64",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center space-y-4",
                        children: [
                            /*#__PURE__*/ _jsxs(Alert, {
                                variant: "destructive",
                                className: "max-w-md mx-auto",
                                children: [
                                    /*#__PURE__*/ _jsx(AlertTriangle, {
                                        className: "h-4 w-4"
                                    }),
                                    /*#__PURE__*/ _jsx(AlertTitle, {
                                        children: "Proposal Invalid"
                                    }),
                                    /*#__PURE__*/ _jsx(AlertDescription, {
                                        children: "This project is not a proposal type. Please access it from the regular project dashboard."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                to: `/project/${project.projectId}`,
                                className: "text-blue-600 hover:underline inline-block",
                                children: "View as Project"
                            })
                        ]
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8 pr-6 flex items-start justify-between w-full",
                    "data-testid": Testids.project.proposalDashboard,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsxs(Link, {
                                    to: "/projects",
                                    className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                    children: [
                                        /*#__PURE__*/ _jsx(ChevronLeft, {
                                            className: "w-4 h-4"
                                        }),
                                        "Projects"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("h1", {
                                    className: "text-xl font-semibold",
                                    children: project.name
                                }),
                                project.description && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground mt-1",
                                    children: project.description
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-4 text-xs text-muted-foreground",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        "Created",
                                        ' ',
                                        new Date(project.createdAt).toLocaleDateString('en-US', {
                                            month: 'long',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: `inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${project.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`,
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: `w-1.5 h-1.5 rounded-full ${project.status === 'active' ? 'bg-green-600' : 'bg-gray-400'}`
                                        }),
                                        project.status
                                    ]
                                }),
                                project.status === 'active' && /*#__PURE__*/ _jsxs(Button, {
                                    size: "sm",
                                    variant: "outline",
                                    onClick: ()=>setConvertDialogOpen(true),
                                    "data-testid": "convert-to-contract-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(ArrowRight, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Convert to Contract"
                                    ]
                                }),
                                project.status === 'active' && /*#__PURE__*/ _jsxs(Button, {
                                    size: "sm",
                                    variant: "outline",
                                    onClick: ()=>setPreAwardDialogOpen(true),
                                    "data-testid": Testids.preAward.convertButton,
                                    children: [
                                        /*#__PURE__*/ _jsx(ArrowRight, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Promote to Pre-Award"
                                    ]
                                }),
                                project.role === 'owner' && project.status !== 'archived' && /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    size: "sm",
                                    onClick: handleArchive,
                                    disabled: archiveProject.isPending,
                                    className: "text-yellow-600 hover:text-yellow-700 border-yellow-300 hover:border-yellow-400 hover:bg-yellow-50",
                                    "data-testid": "header-archive-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(Archive, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Archive"
                                    ]
                                }),
                                project.role === 'owner' && project.status === 'archived' && /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    size: "sm",
                                    onClick: handleUnarchive,
                                    disabled: unarchiveProject.isPending,
                                    className: "text-blue-600 hover:text-blue-700 border-blue-300 hover:border-blue-400 hover:bg-blue-50",
                                    "data-testid": "header-unarchive-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(ArchiveRestore, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Restore"
                                    ]
                                }),
                                project.role === 'owner' && /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    size: "sm",
                                    onClick: ()=>setShowDeleteDialog(true),
                                    disabled: deleteProject.isPending,
                                    className: "text-red-600 hover:text-red-700 border-red-300 hover:border-red-400 hover:bg-red-50",
                                    "data-testid": "header-delete-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(Trash2, {
                                            className: "w-4 h-4 mr-1.5"
                                        }),
                                        "Delete"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConvertToContractDialog, {
                open: convertDialogOpen,
                onOpenChange: setConvertDialogOpen,
                project: project
            }),
            /*#__PURE__*/ _jsx(ConvertToPreAwardDialog, {
                open: preAwardDialogOpen,
                onOpenChange: setPreAwardDialogOpen,
                project: project
            }),
            process.env.NODE_ENV === 'development' && project && /*#__PURE__*/ _jsxs("div", {
                className: "bg-yellow-50 border-b border-yellow-200 px-8 py-1.5 flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-yellow-700 font-medium",
                        children: "Dev"
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        variant: "ghost",
                        size: "sm",
                        className: "h-6 text-xs text-yellow-800 hover:bg-yellow-100",
                        onClick: handleResync,
                        disabled: resyncing,
                        children: [
                            /*#__PURE__*/ _jsx(RefreshCw, {
                                className: `h-3 w-3 mr-1 ${resyncing ? 'animate-spin' : ''}`
                            }),
                            resyncing ? 'Resyncing...' : 'Resync from MinIO'
                        ]
                    })
                ]
            }),
            solicitation && /*#__PURE__*/ _jsx("div", {
                className: "bg-gray-50 border-b border-gray-200 px-8 py-2.5",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center gap-x-6 gap-y-1 text-sm",
                    children: [
                        solicitation.solicitationNumber && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Solicitation #"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: solicitation.solicitationNumber
                                })
                            ]
                        }),
                        solicitation.agency && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Agency"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: solicitation.agency
                                })
                            ]
                        }),
                        solicitation.office && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Office"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: solicitation.office
                                })
                            ]
                        }),
                        solicitation.contractVehicle && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Vehicle"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: solicitation.contractVehicle
                                })
                            ]
                        }),
                        solicitation.dueDate && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Deadline"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: formatDeadline(solicitation.dueDate)
                                })
                            ]
                        }),
                        solicitation.contractType && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Contract Type"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: solicitation.contractType
                                })
                            ]
                        }),
                        solicitation.setAside && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Set-Aside"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: SET_ASIDE_LABELS[solicitation.setAside] || solicitation.setAside
                                })
                            ]
                        }),
                        solicitation.naicsCode && /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "NAICS"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-900",
                                    children: solicitation.naicsCode
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 bg-white flex flex-col overflow-hidden",
                children: /*#__PURE__*/ _jsxs(Tabs, {
                    defaultValue: "files",
                    className: "w-full flex flex-col flex-1 overflow-hidden",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "border-b border-gray-200 bg-white sticky top-0 z-10 flex-shrink-0",
                            children: /*#__PURE__*/ _jsxs(TabsList, {
                                className: "h-auto bg-transparent border-0 rounded-none p-0 gap-0 px-8",
                                children: [
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "files",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Files"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "chat",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Chat"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "team",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Team"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "clins",
                                        "data-testid": Testids.clin.tabTrigger,
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "CLINs"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "lcats",
                                        "data-testid": Testids.lcat.tabTrigger,
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Labor Categories"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "pricing",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Pricing"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "shredview",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Shred View"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "deliverables",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Deliverables"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "qa",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Q&A"
                                    }),
                                    /*#__PURE__*/ _jsx(TabsTrigger, {
                                        value: "master-matrix",
                                        className: "rounded-none border-b-2 border-transparent px-4 py-3 font-medium data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-colors",
                                        children: "Master Matrix"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 overflow-auto min-h-0",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "px-8 py-8 h-full",
                                children: [
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "files",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(FilesTab, {
                                            projectId: project.projectId,
                                            userRole: project.role,
                                            isArchived: project.status === 'archived'
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "chat",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(ChatTab, {
                                            projectId: project.projectId,
                                            userRole: project.role
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "team",
                                        className: "m-0 h-full",
                                        children: user && /*#__PURE__*/ _jsx(TeamTab, {
                                            project: project,
                                            currentUserId: user.id
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "clins",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(CLINsTab, {
                                            projectId: project.projectId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "lcats",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(LCATsTab, {
                                            projectId: project.projectId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "pricing",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(PricingTab, {
                                            projectId: project.projectId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "shredview",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(ShredViewTab, {
                                            projectId: project.projectId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "deliverables",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(ComplianceMatrixTab, {
                                            projectId: project.projectId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "qa",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(QATab, {})
                                    }),
                                    /*#__PURE__*/ _jsx(TabsContent, {
                                        value: "master-matrix",
                                        className: "m-0 h-full",
                                        children: /*#__PURE__*/ _jsx(MasterMatrixTab, {
                                            projectId: project.projectId
                                        })
                                    })
                                ]
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showDeleteDialog,
                onOpenChange: setShowDeleteDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Delete Proposal Permanently?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        "This will ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: "permanently delete"
                                        }),
                                        " ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: project.name
                                        }),
                                        " and all associated data including files, chats, and team members. This action cannot be undone."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleDelete,
                                    className: "bg-red-600 hover:bg-red-700",
                                    children: "Delete Permanently"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
